import { Receivable, TransactionSuggestion } from "@/types/payments";
import { ReceivableApiClient } from "../clients/receivableApiClient";
import { toast } from "sonner";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

// Mock data for receivables
export const mockReceivables: Receivable[] = [
  {
    id: "REC-2023-001",
    status: "unpaid",
    customer: "ABC Corporation",
    amount: 2500.00,
    fxRate: 1.0,
    currency: "USD",
    dueDate: "2025-05-18",
    remittanceInformation: "INV-ABC-001",
  },
  {
    id: "REC-2023-002",
    status: "overdue",
    customer: "Global Industries Ltd.",
    amount: 4200.00,
    fxRate: 1.08,
    currency: "EUR",
    dueDate: "2025-05-01",
    remittanceInformation: "INV-GLOBAL-002",
  },
  {
    id: "REC-2023-003",
    status: "paid",
    customer: "Tech Solutions Inc.",
    amount: 3700.50,
    fxRate: 1.0,
    currency: "USD",
    dueDate: "2025-04-15",
    matched: true,
    transactionId: "TRX-2023-003",
    remittanceInformation: "INV-TECH-003",
  },
  {
    id: "REC-2023-004",
    status: "paid",
    customer: "Retail Chain Co.",
    amount: 1250.00,
    fxRate: 1.0,
    currency: "USD",
    dueDate: "2025-04-20",
    matched: false,
    remittanceInformation: "INV-RETAIL-004",
  },
  {
    id: "REC-2023-005",
    status: "unpaid",
    customer: "Healthcare Group",
    amount: 5800.00,
    fxRate: 1.35,
    currency: "CAD",
    dueDate: "2025-05-25",
    remittanceInformation: "INV-HEALTH-005",
  },
  {
    id: "REC-2023-006",
    status: "paid",
    customer: "Education Services",
    amount: 3200.00,
    fxRate: 1.0,
    currency: "USD",
    dueDate: "2025-04-28",
    matched: true,
    transactionId: "TRX-2023-006",
    remittanceInformation: "INV-EDU-006",
  }
];

export class ReceivableService {
  private apiClient: ReceivableApiClient;

  constructor() {
    this.apiClient = new ReceivableApiClient();
  }

  /**
   * Get all receivables
   */
  async getReceivables(): Promise<Receivable[]> {
    // issue #819: never fall back to fabricated mockReceivables on a real API failure —
    // propagate the rejection so the caller can show a real error state and let the user retry.
    return this.apiClient.getReceivables();
  }

  /**
   * Get a specific receivable by ID
   */
  async getReceivable(id: string): Promise<Receivable | null> {
    try {
      const receivable = await this.apiClient.getReceivable(id);
      if (receivable) {
        return receivable;
      }
      throw new Error("No receivable returned from API");
    } catch (error) {
      console.error(`Failed to get receivable ${id} using API, falling back to mock:`, error);
      toast.error(translateOutsideReact('consultantToasts.common.apiError'), {
        description: translateOutsideReact('consultantToasts.receivables.mockFallback', { id }),
      });
      return mockReceivables.find(r => r.id === id) || null;
    }
  }

  /**
   * Update a receivable
   */
  async updateReceivable(id: string, receivableData: Partial<Receivable>): Promise<boolean> {
    try {
      return await this.apiClient.updateReceivable(id, receivableData);
    } catch (error) {
      console.error(`Failed to update receivable ${id} using API:`, error);
      toast.error(translateOutsideReact('consultantToasts.common.apiError'), {
        description: translateOutsideReact('consultantToasts.receivables.updateFailed', { id }),
      });
      return false;
    }
  }

  /**
   * Match a receivable with a transaction
   */
  async matchReceivable(receivableId: string, transactionId: string): Promise<boolean> {
    try {
      return await this.apiClient.matchReceivable(receivableId, transactionId);
    } catch (error) {
      console.error(`Failed to match receivable ${receivableId} with transaction ${transactionId} using API:`, error);
      toast.error(translateOutsideReact('consultantToasts.common.apiError'), {
        description: translateOutsideReact('consultantToasts.receivables.matchFailed', { receivableId, transactionId }),
      });
      return false;
    }
  }

  /**
   * Get the total pending receivables balance in the specified currency
   */
  async getPendingReceivablesBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    try {
      return await this.apiClient.getPendingReceivablesBalance(targetCurrency);
    } catch (error) {
      console.error('Failed to get pending receivables balance using API, falling back to mock:', error);
      throw error;
    }
  }

  async markAsReceivedExternally(receivableId: string, receivedDate: string, actualSekAmount?: number): Promise<boolean> {
    return await this.apiClient.markAsReceivedExternally(receivableId, receivedDate, actualSekAmount);
  }

  /**
   * Acknowledge a receivable as received externally when a bank account is connected but no
   * matching transaction was found — or none of the shown candidates were correct (issue #498,
   * Task 4 — the receivables-side equivalent of PaymentService.acknowledgeExternalPayment).
   * Distinct from markAsReceivedExternally above (case a, trusted write, no audit row): the
   * backend always writes an auto_match_decisions audit row for this call. Deliberately does NOT
   * catch-and-toast like most other methods on this class (getReceivables,
   * getPendingReceivablesBalance, etc.) — this is a real financial-state write, so the caller
   * (MarkAsReceivedModal via AccountReceivables.tsx) needs the rejection to know the write did
   * not happen, matching PaymentService.acknowledgeExternalPayment's convention.
   */
  async acknowledgeExternalPayment(receivableId: string, receivedDate: string): Promise<{ decisionId: string }> {
    return await this.apiClient.acknowledgeExternalPayment(receivableId, receivedDate);
  }

  async getTransactionSuggestions(receivableId: string): Promise<TransactionSuggestion[]> {
    return await this.apiClient.getTransactionSuggestions(receivableId);
  }

  async getPrecomputedTransactionSuggestions(receivableId: string): Promise<TransactionSuggestion[]> {
    return await this.apiClient.getPrecomputedTransactionSuggestions(receivableId);
  }
}
